import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Square, CalendarBlank } from "@phosphor-icons/react";
import { useApi, errMsg, nzDate } from "@/lib/api";
import { Section, Empty, Pill, useLoad } from "@/components/app/common";

const SRC = { task: "Task", compliance: "Compliance", machinery: "Service" };
const fmtTime = (t) => { if (!t) return ""; const [h, m] = t.split(":").map(Number); return `${h % 12 || 12}${m ? `:${String(m).padStart(2, "0")}` : ""}${h < 12 ? "am" : "pm"}`; };
const dayName = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short" });

function FarmWeek({ f, month, multi, onTick }) {
  const api = useApi();
  const tick = async (p) => {
    try { await api.post(`/policy-items/${p.id}/check`, { month, done: true }); toast.success(`Ticked: ${p.title}`); onTick(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const dated = [...f.events.map((e) => ({ ...e, kind: "event" })), ...f.auto.map((a) => ({ ...a, kind: "auto" }))]
    .sort((a, b) => (a.date + (a.time || "99")).localeCompare(b.date + (b.time || "99")));
  if (multi && !dated.length && !f.policy.length) return null;
  return (
    <div className={multi ? "border border-slate-200 rounded-lg p-4" : ""} data-testid={`week-farm-${f.farm_id}`}>
      {multi && <p className="font-semibold mb-2">{f.farm_name}</p>}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <p className="label-sm mb-2">Events &amp; due this week</p>
          {dated.length === 0 ? <Empty>Nothing on this week.</Empty> : (
            <ul className="space-y-1.5">{dated.map((it) => (
              <li key={`${it.kind}-${it.id}`} className="text-sm flex gap-3" data-testid={`week-item-${it.kind}-${it.id}`}>
                <span className={`w-24 shrink-0 text-xs pt-0.5 ${it.overdue ? "text-red-600 font-semibold" : "text-slate-500"}`}>{it.overdue ? `Overdue ${nzDate(it.date)}` : dayName(it.date)}</span>
                {it.kind === "event"
                  ? <Link to="/calendar" className="hover:underline"><b>{it.time ? `${fmtTime(it.time)} ` : ""}{it.title}</b>{it.who_where ? <span className="text-slate-500"> · {it.who_where}</span> : null}</Link>
                  : <Link to={it.link} className="hover:underline">{it.title} <Pill tone={it.overdue ? "red" : "slate"}>{SRC[it.source]}</Pill></Link>}
              </li>))}</ul>)}
        </div>
        <div>
          <p className="label-sm mb-2">Policy items still to tick this month</p>
          {f.policy.length === 0 ? <Empty>All ticked off.</Empty> : (
            <ul className="space-y-1.5">{f.policy.map((p) => (
              <li key={p.id} className="text-sm flex items-start gap-2" data-testid={`week-policy-${p.id}`}>
                <button onClick={() => tick(p)} className="text-slate-400 hover:text-green-600 transition-colors" aria-label={`Tick ${p.title}`} data-testid={`week-policy-${p.id}-tick`}><Square size={20} /></button>
                <span>{p.title}{p.task && <span className="text-xs text-slate-500"> · task due {nzDate(p.task.due_date)}</span>}</span>
              </li>))}</ul>)}
        </div>
      </div>
    </div>
  );
}

export function ThisWeek({ farmId = "all", groupId = "all" }) {
  const api = useApi();
  const [d, reload] = useLoad(() => api.get(`/calendar/week?farm_id=${farmId}&group_id=${groupId || "all"}`), [farmId, groupId]);
  if (!d) return null;
  const multi = farmId === "all";
  const shown = d.farms.filter((f) => !multi || f.events.length || f.auto.length || f.policy.length);
  return (
    <div className="mb-6"><Section title={`This week · ${nzDate(d.start)} – ${nzDate(d.end)}`} testid="dash-this-week"
      actions={<Link to="/calendar" className="text-sm font-semibold inline-flex items-center gap-1.5 hover:underline" data-testid="dash-open-calendar"><CalendarBlank size={16} />Calendar</Link>}>
      {shown.length === 0 ? <Empty>Nothing on this week and every policy item is ticked.</Empty>
        : <div className="space-y-4">{shown.map((f) => <FarmWeek key={f.farm_id} f={f} month={d.month} multi={multi} onTick={reload} />)}</div>}
    </Section></div>
  );
}
